import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { api } from '../../services/api';

// Lyra — the manager thread. The quest log, written as messages from a person.
//
// A list of acts with statuses is a quest log; the same information in her voice
// is a relationship. It also solves a story problem that a status column can't:
// **she only tells you what's next.** Acts you haven't reached simply aren't in
// the thread, so the chain can't be read ahead, and finishing one feels like she
// answered rather than like a row changed colour.
//
// THE WALK lives here too (Build_Game_Intro §4.4): when the session is next,
// her message IS the producer lineup — photo, name, one trait each, and the
// player picks on instinct. Choice, not assignment.

// The chain, as she'd say it. Keyed by quest slug — a new act adds a line here
// and nothing else, and an act with no line just doesn't get a message.
const LINES = {
  come_up_arrival: "I'm on the ground with you. Studio on 14th, tonight — the board doesn't wait.",
  come_up_the_ride: "Ride's handled — Spor1, HIMSELF. Yes, that Spor1. Yellow Lotus, parked off the main strip, impossible to miss. Go find him, get in, and let him talk.",
  come_up_the_look: "He'll drop you at Sassy's. If you're walking into that studio, you're walking in as somebody — she'll make sure of it. Tell her I called ahead. I did.",
  come_up_the_session: "Doors open. Here's who's at a board tonight — pick whoever feels right, they're all expecting my call.",
  come_up_the_drop: "Now walk outside. Trust me.",
  come_up_open_city: "That's yours now. Whole city — go find out what's in it.",
  come_up_unfinished_look: "You never finished at Sassy's. She kept the chair for you.",
};

// Which app (if any) a message hands you off to — so the thread is a way in,
// not just a readout.
const OPENS = { come_up_the_ride: 'rides' };

const CHAIN = 'come_up';

// Mirrors the chain_order as REORDERED by migration 277 (ride before salon —
// Build_Game_Intro §2). Only used to tell a COMPLETED act apart from one you
// haven't reached — the server never sends the row back once it's done, so its
// position is the only thing left to reason from.
const CHAIN_ORDER = {
  come_up_arrival: 0,
  come_up_the_ride: 1,
  come_up_the_look: 2,
  come_up_the_session: 3,
  come_up_the_drop: 4,
  come_up_open_city: 5,
};

export default function PhoneLyra({ quests, onRefresh }) {
  const setPhoneApp = useWorldStore((s) => s.setPhoneApp);
  const producer = useWorldStore((s) => s.producer);
  const setProducer = useWorldStore((s) => s.setProducer);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const mine = (quests || []).filter((q) => q.chain_id === CHAIN && LINES[q.slug]);

  // The lineup, fetched only when her session message is actually on screen.
  const sessionUp = mine.some((q) => q.slug === 'come_up_the_session');
  const chosen = producer && String(producer.avatarId) === String(myAvatarId) ? producer : null;
  const [lineup, setLineup] = useState([]);
  useEffect(() => {
    if (!sessionUp || chosen) return undefined;
    let dead = false;
    api.dialogueProducers()
      .then((d) => { if (!dead) setLineup(d?.producers || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, [sessionUp, !!chosen]); // eslint-disable-line react-hooks/exhaustive-deps

  // A completed non-repeatable quest drops out of `availableFor` entirely, so
  // "absent" means completed OR not yet reachable. Chain order tells them apart:
  // anything ordered BELOW something you can currently see is behind you.
  const frontier = mine.reduce((max, q) => Math.max(max, Number(q.chain_order) || 0), -1);
  const done = Object.keys(LINES).filter((slug) => {
    if (mine.some((q) => q.slug === slug)) return false;
    const order = CHAIN_ORDER[slug];
    return order !== undefined && frontier >= 0 && order < frontier;
  });

  const ordered = [...mine].sort((a, b) => (Number(a.chain_order) || 0) - (Number(b.chain_order) || 0));

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <span style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(160deg,#ffb25e,#ff5e78)', color: '#1a0f00',
          display: 'grid', placeItems: 'center', font: '800 15px system-ui',
        }}>L</span>
        <span style={{ font: '600 11px system-ui', opacity: 0.55 }}>Manager · always on</span>
        <button
          onClick={() => onRefresh?.()}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,233,196,0.5)', fontSize: 14 }}
          aria-label="Refresh"
        >⟳</button>
      </div>

      {done.map((slug) => (
        <div key={slug} style={{ ...bubble, opacity: 0.34, marginBottom: 8 }}>
          <div style={{ font: '600 12.5px system-ui', lineHeight: 1.45, textDecoration: 'none' }}>{LINES[slug]}</div>
          <div style={{ font: '700 10px system-ui', marginTop: 5, color: '#7dff9d' }}>✓ Done</div>
        </div>
      ))}

      {ordered.length === 0 && done.length === 0 && (
        <div style={{ font: '600 12.5px system-ui', opacity: 0.45, lineHeight: 1.6, marginTop: 26, textAlign: 'center' }}>
          Nothing from her yet.<br />She'll call.
        </div>
      )}

      {ordered.map((q) => {
        const app = OPENS[q.slug];
        const active = q.status === 'active';
        const isSession = q.slug === 'come_up_the_session';
        return (
          <div
            key={q.slug}
            onClick={app ? () => setPhoneApp(app) : undefined}
            role={app ? 'button' : undefined}
            style={{
              ...bubble, marginBottom: 8, cursor: app ? 'pointer' : 'default',
              border: `1px solid ${active ? 'rgba(255,178,94,0.55)' : 'rgba(255,233,196,0.16)'}`,
              background: active ? 'rgba(255,178,94,0.11)' : 'rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ font: '600 12.5px system-ui', lineHeight: 1.45 }}>
              {isSession && chosen
                ? `Booked you with ${chosen.name}. Walk — it's not far, and look around on the way. Text me when you hear it back.`
                : LINES[q.slug]}
            </div>

            {/* THE LINEUP (§4.4) — inside her message, because the roster IS
                the message. Faces first: you pick a collaborator on instinct.
                The genre badge says what somebody is KNOWN for and gates
                nothing — Lyra's line below says so out loud, because a badge
                that looks like a filter will be read as one. */}
            {isSession && !chosen && lineup.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                <div style={{ font: '600 10.5px system-ui', opacity: 0.6, lineHeight: 1.4, marginBottom: 1 }}>
                  Take whoever you want — the genre's just what they're known for.
                </div>
                {lineup.map((p) => (
                  <button
                    key={p.key}
                    disabled={p.available === false}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (p.available === false) return;
                      // `avatarId` on a stored pick means WHOSE PICK IT IS (the
                      // player's artist) — SceneDock scopes on it — so spreading
                      // the row would silently overwrite the producer's own id
                      // with the player's. Kept apart under its own name; the
                      // studio's video panel needs to know which artist it is
                      // actually showing.
                      setProducer({ ...p, producerAvatarId: p.avatarId, avatarId: myAvatarId });
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                      padding: '8px 10px', borderRadius: 12,
                      cursor: p.available === false ? 'default' : 'pointer',
                      border: `1px solid ${p.accent || 'rgba(255,233,196,0.3)'}`,
                      background: 'rgba(255,255,255,0.05)', color: '#ffe9c4',
                      opacity: p.available === false ? 0.42 : 1,
                    }}
                  >
                    {/* THEIR REAL FACE when the avatar has one — these are roster
                        artists now, not invented names, and a photo is most of
                        why you pick somebody on instinct. Initial disc is the
                        fallback, same as everywhere else. */}
                    {p.image ? (
                      <img src={p.image} alt="" width={34} height={34}
                        style={{ borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
                    ) : (
                      <span style={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(160deg, ${p.accent || '#ffb25e'}, rgba(255,94,120,0.85))`,
                        color: '#1a0f00', display: 'grid', placeItems: 'center', font: '800 14px system-ui',
                      }}>{(p.name || '?').charAt(0)}</span>
                    )}
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', font: '700 12.5px system-ui' }}>{p.name}</span>
                      {/* GENRE IS A BADGE, NOT A GATE (Khari, 2026-08-04: "The
                          genres are a helper (show as badge), but let it be known
                          that any avatar can be chosen"). What they are known
                          for — never a filter on who will take your session. */}
                      {p.genre && (
                        <span style={{
                          display: 'inline-block', marginTop: 2, padding: '1px 6px', borderRadius: 999,
                          font: '700 9px system-ui', letterSpacing: 0.4, textTransform: 'uppercase',
                          border: `1px solid ${p.accent || 'rgba(255,233,196,0.3)'}`, opacity: 0.85,
                        }}>{p.genre}</span>
                      )}
                    </span>
                    <span style={{
                      marginLeft: 'auto', font: '700 9.5px system-ui', whiteSpace: 'nowrap',
                      color: p.available === false ? 'rgba(255,233,196,0.5)' : '#7dff9d',
                    }}>
                      {p.available === false ? (p.busyReason || 'on a gig') : 'free tonight'}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={{ font: '700 10px system-ui', color: active ? '#ffd27d' : 'rgba(255,233,196,0.5)' }}>
                {active ? '● In progress' : 'Waiting on you'}
              </span>
              {app && <span style={{ marginLeft: 'auto', font: '700 10.5px system-ui', color: '#ffb25e' }}>Open {app} →</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const bubble = { padding: '10px 12px', borderRadius: 14, border: '1px solid rgba(255,233,196,0.16)', background: 'rgba(255,255,255,0.06)' };
